import React from 'react';
import styled from 'styled-components';
import { FiActivity, FiAward, FiLayers } from 'react-icons/fi';
import ScrollAnimation from './ScrollAnimation';

const Section = styled.section`
  position: relative;
  overflow: hidden;
  background: ${p => p.theme.colors.gradient.primary};
  color: ${p => p.theme.colors.light};
`;

const GridBackdrop = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
  /* soft grid pattern */
  background-image:
    radial-gradient(circle at 25% 20%, rgba(255,255,255,0.12) 0%, rgba(255,255,255,0) 60%),
    linear-gradient(rgba(255,255,255,0.06) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255,255,255,0.06) 1px, transparent 1px);
  background-size: 100% 100%, 36px 36px, 36px 36px;
  background-position: 0 0, 0 0, 0 0;
`;

const Container = styled.div`
  max-width: ${p => p.theme.breakpoints.wide};
  margin: 0 auto;
  /* Compact variant: allow extra top space so title isn't flush under navbar */
  padding: ${p => p.$compact
    ? `${p.$pt || p.theme.spacing.xl} ${p.theme.spacing.lg} ${p.theme.spacing.lg}`
    : `${p.theme.spacing['2xl']} ${p.theme.spacing.lg}`};

  @media (max-width: ${p => p.theme.breakpoints.tablet}) {
    padding: ${p => p.$compact
      ? `${p.$ptTablet || p.theme.spacing.md} ${p.theme.spacing.md} ${p.theme.spacing.md}`
      : `${p.theme.spacing.xl} ${p.theme.spacing.md}`};
  }
  @media (max-width: ${p => p.theme.breakpoints.mobile}) {
    padding: ${p => p.$compact
      ? `${p.$ptMobile || p.theme.spacing.lg} ${p.theme.spacing.sm} ${p.theme.spacing.sm}`
      : `${p.theme.spacing.lg} ${p.theme.spacing.sm}`};
  }
`;

const Grid = styled.div`
  display: grid;
  /* Give a bit more width to the illustration column without increasing height */
  grid-template-columns: 1.35fr 1.05fr;
  align-items: center;
  gap: ${p => p.theme.spacing['2xl']};

  @media (max-width: ${p => p.theme.breakpoints.desktop}) {
    grid-template-columns: 1fr;
    gap: ${p => p.theme.spacing.lg};
  }

  @media (max-width: ${p => p.theme.breakpoints.tablet}) {
    gap: ${p => p.theme.spacing.md};
  }
`;

const Left = styled.div`
  h1 {
    color: ${p => p.theme.colors.light};
    margin: 0 0 ${p => p.theme.spacing.lg};
    font-size: clamp(24px, 6vw, 42px);
    font-weight: ${p => p.theme.fontWeights.extrabold};
    line-height: 1.1;
    
    @media (max-width: ${p => p.theme.breakpoints.mobile}) {
      margin-bottom: ${p => p.theme.spacing.md};
      line-height: 1.15;
    }
  }
  p {
    max-width: 680px;
    font-size: clamp(${p => p.theme.fontSizes.base}, 1.6vw, ${p => p.theme.fontSizes.xl});
    opacity: 0.92;
    line-height: 1.6;
    margin: 0;
    
    @media (max-width: ${p => p.theme.breakpoints.mobile}) {
      line-height: 1.55;
    }
  }
`;

const Right = styled.div`
  position: relative;
  height: ${p => p.rightHeight || '480px'};

  @media (max-width: ${p => p.theme.breakpoints.desktop}) {
    height: ${p => p.rightHeightTablet || '400px'};
  }
  @media (max-width: ${p => p.theme.breakpoints.tablet}) {
    height: ${p => p.rightHeightMobile || '320px'};
  }
`;

const Card = styled.div`
  position: absolute;
  border-radius: 16px;
  background: rgba(255,255,255,0.18);
  border: 1px solid rgba(255,255,255,0.28);
  box-shadow: 0 18px 40px rgba(0,0,0,0.18);
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
  color: ${p => p.theme.colors.dark};
`;

const CardLarge = styled(Card)`
  inset: auto 0 0 auto;
  right: 24px;
  width: min(500px, 100%);
  height: 280px;
  padding: 22px;
  display: grid;
  grid-template-rows: auto 1fr auto;
`;

const CardSmall = styled(Card)`
  top: 0;
  left: 0;
  width: 260px;
  height: 140px;
  padding: 16px;
  transform: translate(-10%, -10%);
`;

const PlaceholderLine = styled.div`
  height: 10px;
  background: rgba(255,255,255,0.65);
  border-radius: 999px;
  margin-bottom: 10px;
`;

const BadgeRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  color: ${p => p.theme.colors.light};
  opacity: 0.95;
  font-weight: 600;
  margin-top: 12px;

  span {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    background: rgba(255,255,255,0.18);
    border: 1px solid rgba(255,255,255,0.28);
    padding: 8px 12px;
    border-radius: 999px;
    backdrop-filter: blur(6px);
  }
`;

const IllustrationBox = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;

  @media (max-width: ${p => p.theme.breakpoints.mobile}) {
    align-items: flex-start; /* bring illustration closer to text on small screens */
  }
`;

const IllustrationImg = styled.img`
  max-width: 110%;
  max-height: 110%;
  width: auto;
  height: auto;
  object-fit: contain;
  display: block;
  transform: scale(1.1);
  
  @media (max-width: ${p => p.theme.breakpoints.tablet}) {
    max-width: 105%;
    max-height: 105%;
    transform: scale(1.05);
  }
  
  @media (max-width: ${p => p.theme.breakpoints.mobile}) {
    max-width: 100%;
    max-height: 100%;
    transform: scale(1);
  }
`;

export default function PageHero({ 
  title, 
  subtitle, 
  badges = [], 
  children, 
  hideRight = false,
  illustrationSrc,
  illustrationAlt,
  rightHeight,
  rightHeightTablet,
  rightHeightMobile,
  compact = false,
  topPadding,
  topPaddingTablet,
  topPaddingMobile
}) {
  return (
    <Section>
      <GridBackdrop />
      <Container 
        $compact={compact}
        $pt={topPadding}
        $ptTablet={topPaddingTablet}
        $ptMobile={topPaddingMobile}
      >
        <Grid>
          <ScrollAnimation animation="fadeInUp">
            <Left>
              <h1>{title}</h1>
              {subtitle && <p>{subtitle}</p>}
              {badges.length > 0 && (
                <BadgeRow>
                  {badges.map((b, i) => (
                    <span key={i}>{b.icon}{b.text}</span>
                  ))}
                </BadgeRow>
              )}
              {children}
            </Left>
          </ScrollAnimation>
          {!hideRight && (
            <ScrollAnimation animation="fadeInUp" delay={200}>
              <Right aria-hidden rightHeight={rightHeight} rightHeightTablet={rightHeightTablet} rightHeightMobile={rightHeightMobile}>
              {illustrationSrc ? (
                <IllustrationBox>
                  <IllustrationImg 
                    src={illustrationSrc}
                    alt={illustrationAlt || title}
                    loading="eager"
                    decoding="async"
                  />
                </IllustrationBox>
              ) : (
                <>
                  <CardLarge>
                    <PlaceholderLine style={{ width: '60%' }} />
                    <div style={{ display: 'grid', gap: 8 }}>
                      <PlaceholderLine style={{ width: '90%', height: 8 }} />
                      <PlaceholderLine style={{ width: '75%', height: 8 }} />
                      <PlaceholderLine style={{ width: '85%', height: 8 }} />
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <span style={{ height: 28, borderRadius: 999, padding: '6px 10px', background: 'rgba(255,255,255,0.7)' }} />
                      <span style={{ height: 28, borderRadius: 999, padding: '6px 10px', background: 'rgba(255,255,255,0.6)' }} />
                    </div>
                  </CardLarge>
                  <CardSmall>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: '#111827' }}>
                      <FiActivity /> <div style={{ fontWeight: 700 }}>Live</div>
                    </div>
                    <div style={{ display: 'flex', gap: 6, marginTop: 10 }}>
                      <FiLayers /> <FiAward />
                    </div>
                  </CardSmall>
                </>
              )}
            </Right>
            </ScrollAnimation>
          )}
        </Grid>
      </Container>
    </Section>
  );
}
